import type { AuditEventsResponse } from './auditTypes';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api/v1';

export interface AuditEventsQuery {
  page?: number;
  limit?: number;
  action?: string;
  userId?: string;
}

export interface LoginSuccessResponse {
  success: true;
  token: string;
}

export interface LoginMfaRequiredResponse {
  success: true;
  mfaRequired: true;
  userId: string;
  message?: string;
}

export type LoginResponse = LoginSuccessResponse | LoginMfaRequiredResponse;

export interface MeResponse {
  user: {
    sub: string;
    email: string;
    role: string;
    mfa?: boolean;
  };
}

export interface HealthResponse {
  status: string;
  uptime: number;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<LoginResponse> & { error?: string };

  if (!res.ok || !data || data.success !== true) {
    throw new Error(data.error || 'Login failed');
  }

  return data as LoginResponse;
}

export async function validateMfa(userId: string, code: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/auth/mfa/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, token: code }),
  });

  const data = (await res.json().catch(() => ({}))) as { success?: boolean; token?: string; error?: string };

  if (!res.ok || !data.success || !data.token) {
    throw new Error(data.error || 'MFA validation failed');
  }

  return data.token;
}

export async function getMe(token: string): Promise<MeResponse> {
  const res = await fetch(`${API_BASE_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to load user');
  }
  return (await res.json()) as MeResponse;
}

export async function getHealth(): Promise<HealthResponse> {
  const res = await fetch(`${API_BASE_URL}/health`);
  if (!res.ok) {
    throw new Error('Health check failed');
  }
  return (await res.json()) as HealthResponse;
}

export async function listAuditEvents(
  token: string,
  query: AuditEventsQuery = {}
): Promise<AuditEventsResponse> {
  const url = new URL(`${API_BASE_URL}/audit/events`);
  const page = query.page ?? 1;
  const limit = query.limit ?? 20;
  url.searchParams.set('page', String(page));
  url.searchParams.set('limit', String(limit));
  if (query.action) {
    url.searchParams.set('action', query.action);
  }
  if (query.userId) {
    url.searchParams.set('userId', query.userId);
  }

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to load audit events');
  }

  return (await res.json()) as AuditEventsResponse;
}
